import { collection, addDoc, doc, setDoc, getDocs, getDoc, query, where, orderBy, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Collection names
const COLLECTIONS = {
  WORD_PAIRS: 'wordPairs',
  GAME_SESSIONS: 'gameSession',
  WORDS: 'Words',
  SAVED_IMAGES: 'saved_images',
  SEEK_AND_FIND_IMAGES: 'seek_and_find_images',
  WORD_CONNECTIONS: 'word_connections',
  STORY_IMAGES: 'story_images'
};

// Word pairs data from WordMatchGame component
export const EASY_WORD_PAIRS = [
  { left: 'Hund', right: 'Knochen' },
  { left: 'Sonne', right: 'Himmel' },
  { left: 'Ball', right: 'Spielen' },
  { left: 'Buch', right: 'Lesen' },
];

export const MEDIUM_WORD_PAIRS = [
  { left: 'Wasser', right: 'Strand' },
  { left: 'Sonne', right: 'Himmel' },
  { left: 'Katze', right: 'Maus' },
  { left: 'Regen', right: 'Schirm' },
  { left: 'Blume', right: 'Garten' },
  { left: 'Vogel', right: 'Nest' },
];

export const HARD_WORD_PAIRS = [
  { left: 'Wissenschaft', right: 'Forschung' },
  { left: 'Demokratie', right: 'Wahlen' },
  { left: 'Symphonie', right: 'Orchester' },
  { left: 'Photosynthese', right: 'Pflanzen' },
  { left: 'Gravitation', right: 'Schwerkraft' },
  { left: 'Architektur', right: 'Gebäude' },
  { left: 'Philosophie', right: 'Denken' },
  { left: 'Technologie', right: 'Innovation' },
];

// Initialize word pairs in Firebase
export const initializeWordPairsInDatabase = async () => {
  try {
    const difficulties = [
      { difficulty: 'easy', pairs: EASY_WORD_PAIRS },
      { difficulty: 'medium', pairs: MEDIUM_WORD_PAIRS },
      { difficulty: 'hard', pairs: HARD_WORD_PAIRS },
    ];

    for (const { difficulty, pairs } of difficulties) {
      const docRef = doc(db, COLLECTIONS.WORD_PAIRS, difficulty);
      await setDoc(docRef, {
        difficulty,
        pairs,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }
  } catch (error) {
    console.error('Error initializing word pairs in database:', error);
    throw error;
  }
};

// Get word pairs by difficulty (returns pairs with word IDs)
export const getWordPairsByDifficulty = async (difficulty) => {
  try {
    // Try to get the document by ID first (since documents are stored with difficulty as ID)
    const docRef = doc(db, COLLECTIONS.WORD_PAIRS, difficulty);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.pairs || [];
    }
    
    // Fallback to hardcoded pairs if not in database
    switch (difficulty) {
      case 'easy': return EASY_WORD_PAIRS;
      case 'hard': return HARD_WORD_PAIRS;
      default: return MEDIUM_WORD_PAIRS;
    }
  } catch (error) {
    console.error('❌ Error getting word pairs from database:', error);
    // Fallback to hardcoded pairs
    switch (difficulty) {
      case 'easy': return EASY_WORD_PAIRS;
      case 'hard': return HARD_WORD_PAIRS;
      default: return MEDIUM_WORD_PAIRS;
    }
  }
};

// Helper function to get word data by reference ID
const getWordDataByRef = async (wordRef) => {
  try {
    const docRef = doc(db, COLLECTIONS.WORDS, wordRef);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const wordData = {
        id: docSnap.id,
        ...docSnap.data()
      };
      return wordData;
    } else {
      return null;
    }
  } catch (error) {
    console.error('❌ Error getting word data by ref:', error);
    return null;
  }
};

// Get word pairs with resolved word data from word_connections collection
export const getWordPairsWithWords = async () => {
  try {
    // Fetch all documents from word_connections collection
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.WORD_CONNECTIONS));
    
    if (querySnapshot.empty) {
      return [];
    }

    // Process each connection document
    const wordPairPromises = querySnapshot.docs.map(async (docSnap) => {
      const connectionData = docSnap.data();
      
      const { word1Ref, word2Ref } = connectionData;
      
      if (!word1Ref || !word2Ref) {
        return null;
      }

      // Fetch word data for both references
      const [word1Data, word2Data] = await Promise.all([
        getWordDataByRef(word1Ref),
        getWordDataByRef(word2Ref)
      ]);

      if (!word1Data || !word2Data) {
        return null;
      }

      return {
        left: word1Data.word || word1Data.german || '',
        right: word2Data.word || word2Data.german || '',
        leftWordId: word1Ref,
        rightWordId: word2Ref,
        leftWordData: word1Data,
        rightWordData: word2Data,
        leftImage: word1Data.linkedImage || word1Data.LinkedImage || null,
        rightImage: word2Data.linkedImage || word2Data.LinkedImage || null
      };
    });

    // Wait for all word pairs to be resolved and filter out nulls
    const resolvedPairs = (await Promise.all(wordPairPromises)).filter(pair => pair !== null);
    
    return resolvedPairs;
  } catch (error) {
    console.error('❌ Error getting word pairs with words:', error);
    return [];
  }
};

// Save game session to database
export const saveGameSession = async (gameSession) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.GAME_SESSIONS), {
      ...gameSession,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error saving game session:', error);
    throw error;
  }
};

// Update game session
export const updateGameSession = async (sessionId, updates) => {
  try {
    const docRef = doc(db, COLLECTIONS.GAME_SESSIONS, sessionId);
    await setDoc(docRef, {
      ...updates,
      updatedAt: new Date(),
    }, { merge: true });
  } catch (error) {
    console.error('Error updating game session:', error);
    throw error;
  }
};

// Get user's game sessions
export const getUserGameSessions = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.GAME_SESSIONS),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting user game sessions:', error);
    throw error;
  }
};

// Get a word by its ID
export const getWordById = async (wordId) => {
  try {
    const docRef = doc(db, COLLECTIONS.WORDS, wordId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      console.error('Word not found with ID:', wordId);
      return null;
    }
  } catch (error) {
    console.error('Error getting word by ID:', error);
    throw error;
  }
};

// Get image by German word from linkedImage field in Words collection
export const getImageByGermanWord = async (germanWord) => {
  try {
    // Search for the word in the Words collection
    const q = query(
      collection(db, COLLECTIONS.WORDS),
      where('word', '==', germanWord)
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const wordDoc = querySnapshot.docs[0];
      const wordData = wordDoc.data();
      
      // Extract linkedImage field
      const linkedImage = wordData.linkedImage || wordData.LinkedImage;
      
      if (linkedImage) {
        const result = {
          id: wordDoc.id,
          word: germanWord,
          imageUrl: linkedImage,
          linkedImage: linkedImage
        };
        return result;
      } else {
        return null;
      }
    } else {
      return null;
    }
  } catch (error) {
    console.error('❌ Error getting image by German word:', error);
    return null;
  }
};

// Get images for multiple German words
export const getImagesByGermanWords = async (germanWords) => {
  try {
    const imagePromises = germanWords.map(word => getImageByGermanWord(word));
    const images = await Promise.all(imagePromises);
    
    // Create a map of word -> image data
    const imageMap = {};
    germanWords.forEach((word, index) => {
      if (images[index]) {
        imageMap[word] = images[index];
      }
    });
    
    return imageMap;
  } catch (error) {
    console.error('❌ Error getting images by German words:', error);
    return {};
  }
};

// Get seek and find images data
export const getSeekAndFindImages = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.SEEK_AND_FIND_IMAGES));
    
    const seekAndFindImages = [];
    querySnapshot.forEach((doc) => {
      const data = {
        id: doc.id,
        ...doc.data()
      };
      seekAndFindImages.push(data);
    });
    
    return seekAndFindImages;
  } catch (error) {
    console.error('❌ Error getting seek and find images:', error);
    return [];
  }
};

// Session-scoped caches — shared across callers so repeated openings of the
// same screen don't re-hit Firestore. Cleared on full page reload.
let allWordsPromise = null;
let allStoryImagesPromise = null;

export const invalidateGameCache = () => {
  allWordsPromise = null;
  allStoryImagesPromise = null;
};

// Get all words from Words collection
export const getAllWords = () => {
  if (allWordsPromise) return allWordsPromise;

  allWordsPromise = (async () => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.WORDS));
      const words = [];
      querySnapshot.forEach((doc) => {
        words.push({ id: doc.id, ...doc.data() });
      });
      return words;
    } catch (error) {
      console.error('❌ Error getting all words:', error);
      allWordsPromise = null; // allow retry on next call
      return [];
    }
  })();

  return allWordsPromise;
};

// Get word data by German word name
export const getWordByGermanName = async (germanWord) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.WORDS),
      where('word', '==', germanWord)
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const result = {
        id: doc.id,
        ...doc.data()
      };
      return result;
    } else {
      return null;
    }
  } catch (error) {
    console.error('❌ Error getting word by German name:', error);
    return null;
  }
};

// Get word data for multiple German words
export const getWordDataByGermanWords = async (germanWords) => {
  try {
    const wordPromises = germanWords.map(word => getWordByGermanName(word));
    const wordsData = await Promise.all(wordPromises);
    
    // Create a map of word -> word data
    const wordMap = {};
    germanWords.forEach((word, index) => {
      if (wordsData[index]) {
        wordMap[word] = wordsData[index];
      }
    });
    
    return wordMap;
  } catch (error) {
    console.error('❌ Error getting word data by German words:', error);
    return {};
  }
};

// Get all saved images
export const getAllSavedImages = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.SAVED_IMAGES));
    
    const images = [];
    querySnapshot.forEach((doc) => {
      const data = {
        id: doc.id,
        ...doc.data()
      };
      images.push(data);
    });
    
    return images;
  } catch (error) {
    console.error('❌ Error getting all saved images:', error);
    return [];
  }
};

// Get all story images from story_images collection
export const getAllStoryImages = () => {
  if (allStoryImagesPromise) return allStoryImagesPromise;

  allStoryImagesPromise = (async () => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.STORY_IMAGES));
      const storyImages = [];
      querySnapshot.forEach((doc) => {
        storyImages.push({ id: doc.id, ...doc.data() });
      });
      return storyImages;
    } catch (error) {
      console.error('❌ Error getting all story images:', error);
      allStoryImagesPromise = null;
      return [];
    }
  })();

  return allStoryImagesPromise;
};

// Normalize laut arrays to lowercase in Words collection
export const normalizeLautArraysToLowercase = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.WORDS));
    
    if (querySnapshot.empty) {
      return { updated: 0, total: 0 };
    }

    let totalDocuments = 0;
    let updatedDocuments = 0;

    // Process each document
    for (const docSnap of querySnapshot.docs) {
      totalDocuments++;
      const data = docSnap.data();
      const docId = docSnap.id;

      // Check if document has laut field and it's an array
      if (data.laut && Array.isArray(data.laut)) {
        // Convert all lauts to lowercase
        const originalLauts = data.laut;
        const normalizedLauts = originalLauts.map(laut => 
          typeof laut === 'string' ? laut.toLowerCase() : laut
        );
        
        // Check if any changes were made
        const hasChanges = originalLauts.some((laut, index) => laut !== normalizedLauts[index]);
        
        if (hasChanges) {
          // Update the document
          const docRef = doc(db, COLLECTIONS.WORDS, docId);
          await updateDoc(docRef, {
            laut: normalizedLauts,
            updatedAt: new Date()
          });
          
          updatedDocuments++;
        }
      }
    }

    const result = { 
      total: totalDocuments, 
      updated: updatedDocuments,
      unchanged: totalDocuments - updatedDocuments 
    };
    
    return result;
  } catch (error) {
    console.error('❌ Error normalizing laut arrays:', error);
    throw error;
  }
};